import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type McProjectionState = 'fresh' | 'stale' | 'error';

/**
 * Derives the projection consistency state for a contract by monitoring
 * the mc_pending_queue table via Realtime subscription.
 * 
 * FRESH: No pending/processing items → projection is up-to-date
 * STALE: Pending or processing items exist → recalculation in progress
 * ERROR: Last queue item failed → projection may be outdated
 */
export function useMcProjectionState(contratoId: string | null | undefined) {
  const [state, setState] = useState<McProjectionState>('fresh');

  const checkState = useCallback(async () => {
    if (!contratoId) { setState('fresh'); return; }

    // Check for pending/processing items (STALE)
    const { data: pending, error: pErr } = await supabase
      .from('mc_pending_queue' as any)
      .select('id, status')
      .eq('contrato_id', contratoId)
      .in('status', ['pending', 'processing'])
      .limit(1);

    if (!pErr && pending && pending.length > 0) {
      setState('stale');
      return;
    }

    // Check for recent failed items (ERROR)
    const { data: failed } = await supabase
      .from('mc_pending_queue' as any)
      .select('id')
      .eq('contrato_id', contratoId)
      .eq('status', 'failed')
      .order('created_at', { ascending: false })
      .limit(1);

    if (failed && failed.length > 0) {
      setState('error');
      return;
    }

    setState('fresh');
  }, [contratoId]);

  // Initial check
  useEffect(() => {
    checkState();
  }, [checkState]);

  // Realtime subscription on mc_pending_queue
  useEffect(() => {
    if (!contratoId) return;

    const channel = supabase
      .channel(`mc-state-${contratoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mc_pending_queue',
          filter: `contrato_id=eq.${contratoId}`,
        },
        () => {
          checkState();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [contratoId, checkState]);

  return state;
}
